import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { api } from '@consts/url.const';
import {
  AccountDetails,
  MonitoringHttpModels,
  MonitoringModels,
} from '../pages/monitoring/models/monitoring.models';
import { Observable } from 'rxjs';
import {
  TagsZones,
} from '../pages/monitoring/modals/add-devices-modal/add-devices-modal.component';
import Vacation = MonitoringModels.Vacation;
import PolicyCreateData = MonitoringModels.PolicyCreateData;

@Injectable()
export class MonitoringService {
  constructor(private http: HttpClient) {}

  public getData(): Observable<MonitoringModels.Account[]> {
    return this.http.get<MonitoringModels.Account[]>(api.monitoring.baseUrl);
  }

  public getPartitionZones(
    accountId: string,
    partitionId: string,
  ): Observable<MonitoringModels.ZoneModel> {
    return this.http.get<MonitoringModels.ZoneModel>(
      api.monitoring.getPartitionZones(partitionId),
    );
  }

  public getAllOrganizationContacts(): Observable<
    MonitoringModels.OrganizationContact[]
  > {
    return this.http.get<MonitoringModels.OrganizationContact[]>(
      api.monitoring.baseAllOrgContacts,
    );
  }

  public getGrantedOrganizations(accountId: string): Observable<MonitoringModels.GrantedOrganization[]> {
    return this.http.get<MonitoringModels.GrantedOrganization[]>(api.monitoring.getGrantedOrganizations(accountId));
  }

  public createGrantedOrganization(accountId: string, inviteCode: string): Observable<MonitoringModels.GrantedOrganization> {
    return this.http.post<MonitoringModels.GrantedOrganization>(api.monitoring.createGrantedOrganization(accountId), { inviteCode: inviteCode });
  }

  public deleteGrantedOrganization(accountId: string, orgId: string): Observable<boolean> {
    return this.http.delete<boolean>(api.monitoring.deleteGrantedOrganization(accountId, orgId));
  }

  public enableAccount(accountId: string) {
    return this.http.put<MonitoringModels.Account>(
      api.monitoring.enableAccount(accountId),
      {},
    );
  }

  public disableAccount(accountId: string) {
    return this.http.put<MonitoringModels.Account>(
      api.monitoring.disableAccount(accountId),
      {},
    );
  }

  public enablePartition(partitionId: string) {
    return this.http.put<MonitoringModels.Account>(
      api.monitoring.enablePartition(partitionId),
      {},
    );
  }

  public disablePartition(partitionId: string) {
    return this.http.put<MonitoringModels.Account>(
      api.monitoring.disablePartition(partitionId),
      {},
    );
  }

  public getAccountSchedule(
    accountId: string,
  ): Observable<MonitoringModels.AccountSchedule> {
    return this.http.get<MonitoringModels.AccountSchedule>(
      api.monitoring.getAccountSchedule(accountId),
    );
  }

  public getPartitionSchedule(
    partitionId: string,
  ): Observable<MonitoringModels.AccountSchedule> {
    return this.http.get<MonitoringModels.AccountSchedule>(
      api.monitoring.getPartitionSchedule(partitionId),
    );
  }

  public getPartitions(
    accountId: string,
  ): Observable<MonitoringModels.Partition[]> {
    return this.http.get<MonitoringModels.Partition[]>(
      api.monitoring.getPartitions(accountId),
    );
  }

  public getPartition(
    partitionId: string,
  ): Observable<MonitoringModels.Partition> {
    return this.http.get<MonitoringModels.Partition>(
      api.monitoring.getPartition(partitionId),
    );
  }

  public createAccount(
    account: AccountDetails,
  ): Observable<MonitoringHttpModels.CreateAccountResponse> {
    return this.http.post<MonitoringHttpModels.CreateAccountResponse>(
      api.monitoring.baseUrl,
      account,
    );
  }

  public saveSelectedZones(
    accountId: string,
    partitionId: string,
    data: TagsZones,
  ): Observable<MonitoringModels.ZoneModel> {
    return this.http.put<MonitoringModels.ZoneModel>(
      api.monitoring.saveSelectedLocationZones(accountId, partitionId),
      data,
    );
  }

  public updatePartitionSchedule(
    accountId: string,
    partitionId: string,
    accountSchedule: MonitoringModels.AccountScheduleDetails,
  ): Observable<MonitoringModels.AccountSchedule> {
    return this.http.put<MonitoringModels.AccountSchedule>(
      api.monitoring.updatePartitionSchedule(accountId, partitionId),
      accountSchedule,
    );
  }

  public updateAccountSchedule(
    accountId: string,
    accountSchedule: MonitoringModels.AccountScheduleDetails,
  ): Observable<MonitoringModels.AccountSchedule> {
    return this.http.put<MonitoringModels.AccountSchedule>(
      api.monitoring.updateSchedule(accountId),
      accountSchedule,
    );
  }

  public getOne(id: string): Observable<MonitoringModels.Account> {
    return this.http.get<MonitoringModels.Account>(
      api.monitoring.baseOneAccountUrl(id),
    );
  }

  public deleteAccountById(id: string): Observable<boolean> {
    return this.http.delete<boolean>(api.monitoring.baseOneAccountUrl(id));
  }

  public deleteContact(groupId: string, id: string): Observable<boolean> {
    return this.http.delete<boolean>(api.monitoring.deleteContact(groupId, id));
  }

  public deleteContactGroup(groupId: string, accountId: string): Observable<boolean> {
    return this.http.delete<boolean>(api.monitoring.deleteContactGroup(groupId, accountId));
  }

  public deletePartition(partitionId: string): Observable<boolean> {
    return this.http.delete<boolean>(api.monitoring.deletePartition(partitionId));
  }

  public updateAccount(id: string, account: AccountDetails): Observable<void> {
    return this.http.put<void>(api.monitoring.baseOneAccountUrl(id), account);
  }

  public createContact(
    id: string,
    groupId: string,
    contactIds: string[],
  ): Observable<MonitoringModels.AccountContact[]> {
    return this.http.post<MonitoringModels.AccountContact[]>(
      api.monitoring.createContact(id, groupId),
      { contactIds },
    );
  }

  public createContactGroup(
    groupInfo: MonitoringModels.ContactGroupDetails,
  ): Observable<MonitoringModels.ContactGroup> {
    return this.http.post<MonitoringModels.ContactGroup>(
      api.monitoring.createContactGroup,
      groupInfo,
    );
  }

  public createOrgContact(
    contactInfo: MonitoringHttpModels.CreateOrgContactRequest,
  ): Observable<MonitoringModels.OrganizationContact> {
    return this.http.post<MonitoringModels.OrganizationContact>(
      api.monitoring.baseAllOrgContacts,
      contactInfo,
    );
  }

  public updateOrgContact(
    contactId: string,
    contactInfo: MonitoringHttpModels.UpdateOrgContactRequest,
    vacation: Vacation[],
  ): Observable<Boolean> {
    const requestData = {
      ...contactInfo,
      vacation: vacation,
    }
    return this.http.put<Boolean>(
      api.monitoring.baseOneOrgContact(contactId),
      requestData,
    );
  }

  public deleteOrgContact(contactId: string): Observable<Boolean> {
    return this.http.delete<Boolean>(
      api.monitoring.baseOneOrgContact(contactId),
    );
  }

  public updateContact(
    id: string,
    contact: MonitoringHttpModels.UpdateContactRequest,
  ): Observable<MonitoringModels.AccountContact> {
    return this.http.put<MonitoringModels.AccountContact>(
      api.monitoring.updateContact(id),
      contact,
    );
  }

  public updatePartition(
    partitionId: string,
    partitionInfo: MonitoringModels.PartitionDetails,
  ): Observable<MonitoringModels.Partition> {
    return this.http.patch<MonitoringModels.Partition>(
      api.monitoring.updatePartition(partitionId),
      partitionInfo,
    );
  }

  public reorderGroupContacts(
    accountId: string,
    groupId: string,
    order: string[],
  ): Observable<boolean> {
    const requestData = {
      orderedIds: order,
    };
    return this.http.put<boolean>(
      api.monitoring.reorderContacts(accountId, groupId),
      requestData,
    );
  }

  public getContacts(id: string): Observable<MonitoringModels.AccountContact[]> {
    return this.http.get<MonitoringModels.AccountContact[]>(
      api.monitoring.getContacts(id),
    );
  }

  public getContactGroups(
    id: string,
  ): Observable<MonitoringModels.ContactGroup[]> {
    return this.http.get<MonitoringModels.ContactGroup[]>(
      api.monitoring.getContactGroups(id),
    );
  }

  public createPartition(
    id: string,
    partitionInfo: MonitoringModels.PartitionDetails,
  ): Observable<MonitoringModels.Partition> {
    const requestData = {
      ...partitionInfo,
      accountId: id,
    };
    return this.http.post<MonitoringModels.Partition>(
      api.monitoring.createPartition,
      requestData,
    );
  }

  public updateGroup(
    groupId: string,
    name: string,
  ): Observable<MonitoringModels.ContactGroup> {
    return this.http.put<MonitoringModels.ContactGroup>(
      api.monitoring.updateGroup(groupId),
      { name },
    );
  }

  public createPolicy (
    policyData: PolicyCreateData,
    accountId: string,
    partitionId: string,
  ): Observable<MonitoringModels.Policy> {
    return this.http.post<MonitoringModels.Policy>(api.monitoring.createPolicy(accountId, partitionId), policyData);
  }

  public getPolicies (partitionId: string): Observable<MonitoringModels.Policy[]> {
    return this.http.get<MonitoringModels.Policy[]>(api.monitoring.getPolicies(partitionId));
  }

  public updatePolicy (
    policyId: string,
    policyData: PolicyCreateData,
  ): Observable<boolean> {
    return this.http.put<boolean>(api.monitoring.updatePolicy(policyId), policyData);
  }

  public deletePolicy(
    policyId: string,
  ): Observable<boolean> {
    return this.http.delete<boolean>(api.monitoring.deletePolicy(policyId));
  }

  public enablePolicy(policyId: string): Observable<boolean> {
    return this.http.patch<boolean>(
      api.monitoring.enablePolicy(policyId),
      {},
    );
  }

  public disablePolicy(policyId: string): Observable<boolean> {
    return this.http.patch<boolean>(
      api.monitoring.disablePolicy(policyId),
      {},
    );
  }
  public duplicatePolicy(policyId: string): Observable<MonitoringModels.Policy> {
    return this.http.post<MonitoringModels.Policy>(api.monitoring.duplicatePolicy(policyId), {});
  }

}
